import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';
import { Link } from 'react-router-dom';
import {
  BuildingOfficeIcon,
  ShoppingBagIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  CalendarIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import EmptyState from '../../components/ui/EmptyState';
import AccessibleButton from '../../components/ui/AccessibleButton';

const AgentDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentCompanies, setRecentCompanies] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch stats
      const statsResponse = await axios.get('/api/field-agent/auth/stats');
      setStats(statsResponse.data);

      // Fetch recent companies
      const companiesResponse = await axios.get('/api/field-agent/companies?limit=5');
      setRecentCompanies(companiesResponse.data.companies || []);

      // Fetch recent orders
      const ordersResponse = await axios.get('/api/field-agent/orders?limit=5');
      setRecentOrders(ordersResponse.data.orders || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('שגיאה בטעינת נתוני הדשבורד');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) {
      return 'תאריך לא זמין';
    }
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'תאריך לא תקין';
      }
      
      return date.toLocaleDateString('he-IL', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'שגיאה בתאריך';
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { text: 'ממתין', className: 'bg-yellow-100 text-yellow-800' },
      processing: { text: 'בטיפול', className: 'bg-blue-100 text-blue-800' },
      shipped: { text: 'נשלח', className: 'bg-purple-100 text-purple-800' },
      delivered: { text: 'נמסר', className: 'bg-green-100 text-green-800' },
      cancelled: { text: 'בוטל', className: 'bg-red-100 text-red-800' }
    };

    const badge = statusMap[status] || { text: status, className: 'bg-gray-100 text-gray-800' };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.className}`}>
        {badge.text}
      </span>
    );
  };

  if (loading) {
    return (
      <LoadingSpinner 
        fullScreen={false}
        size="xl" 
        color="primary" 
        text="טוען נתוני דשבורד..."
        className="py-12"
      />
    );
  }

  return (
    <div className="space-y-6" role="main" aria-label="דשבורד סוכן מכירות">
      {/* Header */}
      <header className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-lg p-6 sm:p-8 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2">
              דשבורד סוכן מכירות
            </h1>
            <p className="text-sm sm:text-base text-blue-100">
              סקירה כללית של הפעילות וההישגים שלך
            </p>
          </div>
          <div className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5 text-blue-200" />
            <span className="text-sm text-blue-100">
              {new Date().toLocaleDateString('he-IL', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </span>
          </div>
        </div>
      </header>

      {/* Stats Grid - Enhanced */}
      <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
        <div className="bg-white overflow-hidden shadow-xl rounded-xl border border-gray-100 hover:shadow-2xl transition-all duration-200 touch-manipulation group">
          <div className="p-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-3 shadow-lg group-hover:scale-110 transition-transform duration-200">
                <BuildingOfficeIcon className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
              </div>
            </div>
            <dl>
              <dt className="text-xs sm:text-sm font-medium text-gray-600 mb-1">חברות פעילות</dt>
              <dd className="text-2xl sm:text-3xl font-bold text-gray-900">{stats?.companies_count || 0}</dd>
            </dl>
          </div>
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-5 py-3 border-t border-blue-200">
            <Link 
              to="/agent/companies" 
              className="text-sm font-semibold text-blue-700 hover:text-blue-800 flex items-center gap-2 touch-manipulation group/link"
            >
              צפה בכל החברות
              <ArrowRightIcon className="h-4 w-4 group-hover/link:translate-x-[-4px] transition-transform" />
            </Link>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow-xl rounded-xl border border-gray-100 hover:shadow-2xl transition-all duration-200 touch-manipulation group">
          <div className="p-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-3 shadow-lg group-hover:scale-110 transition-transform duration-200">
                <ShoppingBagIcon className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
              </div>
            </div>
            <dl>
              <dt className="text-xs sm:text-sm font-medium text-gray-600 mb-1">הזמנות החודש</dt>
              <dd className="text-2xl sm:text-3xl font-bold text-gray-900">{stats?.monthly_orders || 0}</dd>
            </dl>
          </div>
          <div className="bg-gradient-to-r from-green-50 to-emerald-100 px-5 py-3 border-t border-green-200">
            <Link 
              to="/agent/orders" 
              className="text-sm font-semibold text-green-700 hover:text-green-800 flex items-center gap-2 touch-manipulation group/link"
            >
              צפה בכל ההזמנות
              <ArrowRightIcon className="h-4 w-4 group-hover/link:translate-x-[-4px] transition-transform" />
            </Link>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow-xl rounded-xl border border-gray-100 hover:shadow-2xl transition-all duration-200 touch-manipulation group">
          <div className="p-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl p-3 shadow-lg group-hover:scale-110 transition-transform duration-200">
                <CurrencyDollarIcon className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
              </div>
            </div>
            <dl>
              <dt className="text-xs sm:text-sm font-medium text-gray-600 mb-1">הכנסות החודש</dt>
              <dd className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                {formatCurrency(stats?.monthly_revenue || 0)}
              </dd>
            </dl>
          </div>
          <div className="bg-gradient-to-r from-purple-50 to-pink-100 px-5 py-3 border-t border-purple-200">
            <div className="text-sm flex items-center gap-2 text-purple-700">
              <ArrowTrendingUpIcon className="h-4 w-4" />
              <span className="font-medium">ביצועים חודשיים</span>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow-xl rounded-xl border border-gray-100 hover:shadow-2xl transition-all duration-200 touch-manipulation group">
          <div className="p-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-gradient-to-br from-orange-500 to-red-600 rounded-xl p-3 shadow-lg group-hover:scale-110 transition-transform duration-200">
                <ChartBarIcon className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
              </div>
            </div>
            <dl>
              <dt className="text-xs sm:text-sm font-medium text-gray-600 mb-1">יעדים פעילים</dt>
              <dd className="text-2xl sm:text-3xl font-bold text-gray-900">{stats?.active_targets || 0}</dd>
            </dl>
          </div>
          <div className="bg-gradient-to-r from-orange-50 to-red-100 px-5 py-3 border-t border-orange-200">
            <Link 
              to="/agent/targets" 
              className="text-sm font-semibold text-orange-700 hover:text-orange-800 flex items-center gap-2 touch-manipulation group/link"
            >
              צפה ביעדים
              <ArrowRightIcon className="h-4 w-4 group-hover/link:translate-x-[-4px] transition-transform" />
            </Link>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Recent Companies */}
        <section className="bg-white shadow-xl rounded-xl border border-gray-100 transition-all hover:shadow-2xl" aria-label="חברות אחרונות">
          <div className="px-5 py-6 sm:px-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-2">
                <BuildingOfficeIcon className="h-5 w-5 text-white" />
              </div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                חברות אחרונות
              </h2>
            </div>
          </div>
          <div className="px-5 py-6 sm:p-6">
            {recentCompanies.length === 0 ? (
              <EmptyState
                preset="generic"
                title="אין חברות להצגה"
                message="עדיין לא נוספו חברות למערכת"
                icon={BuildingOfficeIcon}
              />
            ) : (
              <ul className="divide-y divide-gray-200 space-y-3" role="list">
                {recentCompanies.map((item) => (
                  <li 
                    key={item.company.id} 
                    className="py-4 px-3 rounded-lg hover:bg-gray-50 transition-colors touch-manipulation"
                    role="listitem"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <Link
                          to={`/agent/companies/${item.company.id}`}
                          className="text-sm sm:text-base font-semibold text-gray-900 hover:text-blue-600 transition-colors block mb-1"
                        >
                          {item.company.name}
                        </Link>
                        <p className="text-xs sm:text-sm text-gray-500 flex items-center gap-1">
                          <span>מספר עוסק:</span>
                          <span className="font-mono">{item.company.business_number || 'לא זמין'}</span>
                        </p>
                      </div>
                      <div className="text-left flex-shrink-0">
                        <div className="bg-blue-100 text-blue-700 px-2.5 py-1 rounded-lg text-xs font-bold mb-1">
                          {item.stats.orders_count} הזמנות
                        </div>
                        {item.stats.last_order && (
                          <p className="text-xs text-gray-500 flex items-center gap-1">
                            <ClockIcon className="h-3 w-3" />
                            {formatDate(item.stats.last_order.created_at)}
                          </p>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-6">
              <AccessibleButton
                as={Link}
                to="/agent/companies"
                variant="outline"
                fullWidth
                size="md"
                icon={ArrowRightIcon}
                iconPosition="right"
              >
                צפה בכל החברות
              </AccessibleButton>
            </div>
          </div>
        </section>

        {/* Recent Orders */}
        <section className="bg-white shadow-xl rounded-xl border border-gray-100 transition-all hover:shadow-2xl" aria-label="הזמנות אחרונות">
          <div className="px-5 py-6 sm:px-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg p-2">
                <ShoppingBagIcon className="h-5 w-5 text-white" />
              </div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                הזמנות אחרונות
              </h2>
            </div>
          </div>
          <div className="px-5 py-6 sm:p-6">
            {recentOrders.length === 0 ? (
              <EmptyState
                preset="orders"
                title="אין הזמנות להצגה"
                message="עדיין לא נוצרו הזמנות"
                icon={ShoppingBagIcon}
              />
            ) : (
              <ul className="divide-y divide-gray-200 space-y-3" role="list">
                {recentOrders.map((order) => (
                  <li 
                    key={order.id} 
                    className="py-4 px-3 rounded-lg hover:bg-gray-50 transition-colors touch-manipulation"
                    role="listitem"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <Link
                          to={`/agent/orders/${order.id}`}
                          className="text-sm sm:text-base font-semibold text-gray-900 hover:text-green-600 transition-colors block mb-1"
                        >
                          {order.order_number}
                        </Link>
                        <p className="text-xs sm:text-sm text-gray-500 flex items-center gap-1">
                          <BuildingOfficeIcon className="h-3 w-3" />
                          {order.company?.name || 'חברה לא ידועה'}
                        </p>
                      </div>
                      <div className="text-left flex-shrink-0 space-y-2">
                        {getStatusBadge(order.status)}
                        <p className="text-sm sm:text-base font-bold text-gray-900">
                          {formatCurrency(order.total_amount)}
                        </p>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-6">
              <AccessibleButton
                as={Link}
                to="/agent/orders"
                variant="outline"
                fullWidth
                size="md"
                icon={ArrowRightIcon}
                iconPosition="right"
              >
                צפה בכל ההזמנות
              </AccessibleButton>
            </div>
          </div>
        </section>
      </div>

      {/* Quick Actions - Enhanced */}
      <section className="bg-white shadow-xl rounded-xl border border-gray-100 p-5 sm:p-6 transition-all hover:shadow-2xl" aria-label="פעולות מהירות">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg p-2">
            <ArrowRightIcon className="h-5 w-5 text-white" />
          </div>
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">פעולות מהירות</h2>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <AccessibleButton
            as={Link}
            to="/agent/companies"
            variant="primary"
            fullWidth
            size="lg"
            icon={BuildingOfficeIcon}
            iconPosition="left"
            className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl"
            ariaLabel="נהל חברות"
          >
            נהל חברות
          </AccessibleButton>
          <AccessibleButton
            as={Link}
            to="/agent/orders"
            variant="primary"
            fullWidth
            size="lg"
            icon={ShoppingBagIcon}
            iconPosition="left"
            className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 shadow-lg hover:shadow-xl"
            ariaLabel="צפה בהזמנות"
          >
            צפה בהזמנות
          </AccessibleButton>
          <AccessibleButton
            as={Link}
            to="/agent/targets"
            variant="primary"
            fullWidth
            size="lg"
            icon={ChartBarIcon}
            iconPosition="left"
            className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 shadow-lg hover:shadow-xl"
            ariaLabel="עקוב אחרי יעדים"
          >
            עקוב אחרי יעדים
          </AccessibleButton>
        </div>
      </section>
    </div>
  );
};

export default AgentDashboard;


